/**
 * contexts/AuthContext.tsx
 * ─────────────────────────
 * Global authentication state provider.
 *
 * On mount: attempts to restore a stored JWT and hydrate the user profile
 * via GET /api/auth/me.  If the token is expired the fetch fails and the
 * stored token is cleared — user is returned to the login screen.
 *
 * Exposes:
 *   user        — AuthUser | null
 *   token       — raw JWT string | null
 *   isLoading   — true while hydrating on startup
 *   signIn()    — call with email + password; sets token + user on success
 *   signOut()   — clears token + user; navigates to /
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { login, fetchMe } from '@/api/auth';
import { saveToken, loadToken, clearToken } from '@/api/client';
import type { AuthUser, UserRole } from '@/types';

// ── Context type ──────────────────────────────────────────────────────────────

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Role → dashboard route map ────────────────────────────────────────────────

const ROLE_REDIRECT: Record<UserRole, string> = {
  pilot:       '/dashboard/missions',
  manager:     '/dashboard/operations',
  admin:       '/dashboard/operations',
  super_admin: '/dashboard/operations',
  customer:    '/dashboard/inventory',
};

// ── Provider ──────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser]         = useState<AuthUser | null>(null);
  const [token, setToken]       = useState<string | null>(null);
  const [isLoading, setLoading] = useState(true);

  // Track whether we've already attempted hydration to prevent double-runs
  const hydratedRef = useRef(false);

  // ── Startup hydration ─────────────────────────────────────────────────────
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    const stored = loadToken();
    if (!stored) {
      setLoading(false);
      return;
    }

    setToken(stored);
    fetchMe()
      .then((profile) => {
        setUser(profile);
      })
      .catch(() => {
        // Token expired or invalid — clear it silently
        clearToken();
        setToken(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // ── signIn ────────────────────────────────────────────────────────────────
  const signIn = useCallback(async (email: string, password: string) => {
    const tokenResponse = await login(email, password);
    saveToken(tokenResponse.access_token);
    setToken(tokenResponse.access_token);

    const profile = await fetchMe();
    setUser(profile);

    // Role-based redirect after login
    const dest = ROLE_REDIRECT[profile.role] ?? '/';
    navigate(dest, { replace: true });
  }, [navigate]);

  // ── signOut ───────────────────────────────────────────────────────────────
  const signOut = useCallback(() => {
    clearToken();
    setToken(null);
    setUser(null);
    navigate('/', { replace: true });
  }, [navigate]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, token, isLoading, signIn, signOut }),
    [user, token, isLoading, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
